import { Button, createStyles, Grid, makeStyles, TextField, Theme, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useState } from "react";
import { IOrganizationSecondaryDetailsFormValues } from "../_interfaces/IOrganizationSecondaryDetailsFormValues";
import TextFieldWithCharacterLimit from "../../../components/Shared/TextFieldWithCharacterLimit";
import { ORGANIZATION_DESCRIPTION_MAX_LENGTH, ORGANIZATION_MISSION_MAX_LENGTH } from "../../../logic/ValidationChecks/FieldLengths";
import FormLoadingSpinner from "../../../components/Organization/VolunteerOpportunities/FormLoadingSpinner";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        form: {
            position: 'relative'
        },
        button: {
            marginTop: theme.spacing(2),
        },
        alert: {
            margin: theme.spacing(1),
            flexGrow: 1,
        },
        title: {
            color: theme.palette.action.active,
            marginBottom: theme.spacing(3)
        },
        flex: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.up('sm')]: {
                alignItems: 'center'
            }
        },
        textField: {
            margin: theme.spacing(1),
            width: '50ch',
            transition: theme.transitions.create('width'),
            [theme.breakpoints.down('xs')]: {
                width: 'auto',
                flexGrow: 1,
            },
            // '& > .MuiFormHelperText-root': {
            //     whiteSpace: 'pre-line'
            // }
        },
    }),
);

interface OrganizationSecondaryDetailsFormProps {
    values: IOrganizationSecondaryDetailsFormValues;
    onChange: (patch: Partial<IOrganizationSecondaryDetailsFormValues>) => void;
    onSubmit: (overrideValues?: Partial<IOrganizationSecondaryDetailsFormValues>) => void;
    isSubmitting?: boolean;
    error?: string;
    fieldErrors?: Partial<Record<keyof IOrganizationSecondaryDetailsFormValues, string>>;
}

export const OrganizationSecondaryDetailsForm: React.FC<OrganizationSecondaryDetailsFormProps> = ({
    values,
    onChange,
    onSubmit,
    isSubmitting,
    error,
    fieldErrors
}) => {

    /********** State **********/

    const getFormId = () => {
        return `form-organization-secondary-details-${Date.now()}-${Math.random()}`;
    }

    const [formId] = useState(() => getFormId());

    /********** React hooks **********/

    const classes = useStyles();

    /********** Event handlers **********/

    const onFormSubmitted = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); // Prevent page reload
        event.stopPropagation();
        onSubmit();
    }

    const handleMissionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ mission: event.target.value });
    }

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ description: event.target.value });
    }

    const handleWebsiteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ website: event.target.value });
    }

    const missionError = fieldErrors?.mission || "";
    const descriptionError = fieldErrors?.description || "";
    const websiteError = fieldErrors?.website || "";

    /********** Render **********/

    return (
        <form onSubmit={onFormSubmitted} noValidate autoComplete="off" id={formId} className={classes.form}>
            {values.loading ? <FormLoadingSpinner /> : undefined}
            {error && <Alert severity="error" className={classes.alert}>{error}</Alert>}
            <Typography variant="h5" className={classes.title}>Give volunteers some information about your organization.</Typography>
            <div className={classes.flex}>
                {values.isForProfit
                    ? null
                    : <TextFieldWithCharacterLimit
                        characterLimit={ORGANIZATION_MISSION_MAX_LENGTH}
                        TextFieldProps={{
                            id: "standard-mission-input",
                            label: "Mission",
                            variant: "outlined",
                            required: true,
                            disabled: isSubmitting || values.fieldProperties?.mission?.disabled,
                            value: values.mission,
                            onChange: handleMissionChange,
                            autoFocus: true,
                            multiline: true,
                            rows: 2,
                            rowsMax: 5,
                            className: classes.textField,
                            error: missionError.length > 0,
                            helperText:
                                missionError.length > 0
                                    ? missionError
                                    : "In a sentence or two, explain your organization's aim."
                        }}
                    />
                }
                <TextFieldWithCharacterLimit
                    characterLimit={ORGANIZATION_DESCRIPTION_MAX_LENGTH}
                    TextFieldProps={{
                        id: "standard-description-input",
                        label: "Description",
                        variant: "outlined",
                        required: true,
                        multiline: true,
                        rows: 4,
                        rowsMax: 10,
                        value: values.description,
                        onChange: handleDescriptionChange,
                        className: classes.textField,
                        disabled: isSubmitting || values.fieldProperties?.description?.disabled,
                        error: descriptionError.length > 0,
                        helperText:
                            descriptionError.length > 0
                                ? descriptionError
                                : values.isForProfit
                                    ? "Describe what your organization does."
                                    : "Add some more details about what your organization does."
                    }}
                />
                <TextField
                    id="standard-website-input"
                    label="Website"
                    variant="outlined"
                    value={values.website}
                    onChange={handleWebsiteChange}
                    className={classes.textField}
                    disabled={isSubmitting || values.fieldProperties?.website?.disabled}
                    error={websiteError.length > 0}
                    helperText={websiteError.length > 0
                        ? websiteError
                        : "If your organization has a webpage, you can add its link here."}
                />
            </div>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Button
                        color="primary"
                        variant="contained"
                        className={classes.button}
                        size="large"
                        type="submit"
                        form={formId}
                        disabled={isSubmitting}
                    >
                        Continue
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};